import React, { useState } from "react";
import "./../index.css";
import loader from "./../images/loader.gif";
import { firestore } from "./../firebase";
import { saveAs } from "file-saver";
import {
  collection,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
  writeBatch,
} from "firebase/firestore";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { IoMdClose, IoIosRefresh } from "react-icons/io";
import {
  FaGoogle,
  FaApple,
  FaUser,
  FaArrowLeft,
  FaArrowRight,
  FaSearch,
} from "react-icons/fa";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];
const Block = (props) => {
  const type = props.type;
  const data = props.data; // Ensure data structure

  const [showSoloWord, setShowSoloWord] = useState(false);
  const [showAdWord, setShowAdWord] = useState(false);
  const [newWord, setNewWord] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "word",
    direction: "asc",
  });
  const [wordsPagination, setWordsPagination] = useState(20);
  const [userSearch, setUserSearch] = useState("");
  const [wordSearch, setWordSearch] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [userSort, setUserSort] = useState({ key: "wins", direction: "desc" });

  const handleDateChange = (offset) => {
    const parts = props.selectedDate.split("-");
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]);
    const year = parseInt(parts[2]);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + offset);

    let d = date.getDate();
    let m = date.getMonth() + 1;
    let y = date.getFullYear();

    // Format back to D-M-YYYY (original app format)
    props.setSelectedDate(`${d}-${m}-${y}`);
  };

  const setAllUsersOffline = async () => {
    try {
      const batch = writeBatch(firestore);

      data.forEach((user) => {
        const userRef = doc(collection(firestore, "users"), user.id);
        batch.update(userRef, { isOnline: false });
      });

      await batch.commit();
    } catch (error) { }
  };

  const setUserOffline = async (id) => {
    const user = data.find((user) => user.id === id);
    if (
      user &&
      window.confirm(`Are you sure you want to set ${user.username} offline?`)
    ) {
      try {
        const userRef = doc(collection(firestore, "users"), id);
        await updateDoc(userRef, { isOnline: false });
      } catch (error) {
        console.error(`Error setting user ${user.username} offline:`, error);
      }
    }
  };

  const getTimeLapsed = (timestamp) => {
    const now = new Date().getTime();
    const lapsed = now - timestamp;
    const seconds = Math.floor(lapsed / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) {
      return `${days} ${days === 1 ? "day" : "days"}`;
    }
    if (hours > 0) {
      return `${hours} ${hours === 1 ? "hour" : "hours"}`;
    }
    if (minutes > 0) {
      return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    }
    return `${seconds} ${seconds === 1 ? "second" : "seconds"}`;
  };

  const loadMoreWords = async (all = false) => {
    if (all) {
      setWordsPagination(data.words.length);
    } else {
      setWordsPagination(wordsPagination + 20);
    }
  };

  const sortTable = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const addWord = async () => {
    if (newWord.trim() === "") return;
    await addDoc(collection(firestore, "unused_words"), { word: newWord });
    setNewWord("");
    loadMoreWords(true); // Reload words
  };

  const removeWord = async (id, collectionName) => {
    if (window.confirm("Are you sure you want to delete this word?")) {
      await deleteDoc(doc(firestore, collectionName, id));
      loadMoreWords(true); // Reload words
    }
  };

  const toggleSpoiler = (type) => {
    if (type === "solo") {
      setShowSoloWord(!showSoloWord);
    } else if (type === "ad") {
      setShowAdWord(!showAdWord);
    }
  };

  const renderTodaysWordsBlock = () => {
    if (!data) {
      return <div>No words available for today.</div>;
    }

    return (
      <div className="words-block">
        <div className="spoiler-container">
          <div className="spoiler" onClick={() => toggleSpoiler("solo")}>
            {showSoloWord ? data.solo : "Show First Word"}
          </div>
          <div className="spoiler" onClick={() => toggleSpoiler("ad")}>
            {showAdWord ? data.ad : "Show Second Word"}
          </div>
        </div>
      </div>
    );
  };

  const renderOnlineBlock = () => {
    const sortedUsers = [...data].sort((a, b) => {
      if (a.lastOnline && b.lastOnline) {
        return a.lastOnline - b.lastOnline;
      }
      return 0;
    });

    if (sortedUsers.length === 0) {
      return (
        <div className="online-users empty-state">
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            <ion-icon name="people-outline" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}></ion-icon>
            <h2>No online users currently</h2>
          </div>
        </div>
      );
    }

    return (
      <div className="online-users">
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={setAllUsersOffline} className="danger-btn">
            Set All Users Offline
          </button>
        </div>
        <table className="blockTable">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>Type</th>
              <th>Username</th>
              <th>Last Online</th>
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  {user.type === "anonymous" ? (
                    <FaUser size={18} />
                  ) : user.type === "google" ? (
                    <FaGoogle style={{ color: "#EA4335" }} size={18} />
                  ) : (
                    <FaApple size={18} />
                  )}
                </td>
                <td>
                  <a
                    href={`https://console.firebase.google.com/u/0/project/wordle-c7049/firestore/databases/-default-/data/~2Fusers~2F${user.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="user-link"
                    style={{ fontWeight: 500, color: 'var(--primary)' }}
                  >
                    {user.username}
                  </a>
                </td>
                <td>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {user.lastOnline
                      ? `${new Date(user.lastOnline).toLocaleString("he-IL")}`
                      : "N/A"}
                  </span>
                  {user.lastOnline && (
                    <span style={{ fontSize: '0.8rem', marginLeft: '8px', color: 'var(--accent)' }}>
                      ({getTimeLapsed(user.lastOnline)} ago)
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button className="icon-btn" onClick={() => setUserOffline(user.id)}>
                    <IoMdClose size={20} style={{ color: 'var(--error)' }} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderStatsBlock = () => {
    if (!data || data.length === 0) {
      return (
        <div className="stats-block empty">
          <div className="stats-header">
            <h2>No data for period ending {props.selectedDate}</h2>
            <div className="controls-row">
              <div className="range-selector">
                {["1d", "7d", "30d"].map((range) => (
                  <button
                    key={range}
                    className={`range-btn ${props.statsRange === range ? "active" : ""}`}
                    onClick={() => props.setStatsRange(range)}
                  >
                    {range.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className="date-picker-container">
                <button className="icon-btn" onClick={() => handleDateChange(1)}><FaArrowRight /></button>
                <input
                  type="date"
                  value={props.selectedDate.split("-").map(p => p.padStart(2, '0')).reverse().join("-")}
                  onChange={(e) => {
                    let newFormat = e.target.value.split("-").reverse().join("-");
                    if (newFormat.startsWith("0") && newFormat[1] !== "-") newFormat = newFormat.slice(1);
                    props.setSelectedDate(newFormat.replace("-0", "-"));
                  }}
                />
                <button className="icon-btn" onClick={() => handleDateChange(-1)}><FaArrowLeft /></button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Aggregate multiple days
    const aggregated = data.reduce((acc, day) => {
      acc.solo.wins.regular += day.solo?.wins?.regular || 0;
      acc.solo.wins.extra += day.solo?.wins?.extra || 0;
      acc.solo.loses.regular += day.solo?.loses?.regular || 0;
      acc.solo.loses.extra += day.solo?.loses?.extra || 0;

      acc.ad.wins.regular += day.ad?.wins?.regular || 0;
      acc.ad.wins.extra += day.ad?.wins?.extra || 0;
      acc.ad.loses.regular += day.ad?.loses?.regular || 0;
      acc.ad.loses.extra += day.ad?.loses?.extra || 0;

      acc.wordlyPlusPurchases += day.wordlyPlusPurchases || 0;

      return acc;
    }, {
      solo: { wins: { regular: 0, extra: 0 }, loses: { regular: 0, extra: 0 } },
      ad: { wins: { regular: 0, extra: 0 }, loses: { regular: 0, extra: 0 } },
      wordlyPlusPurchases: 0,
    });

    // Prepare trend data
    const trendData = data.map(day => ({
      date: day.date,
      Wins: (day.solo?.wins?.regular || 0) + (day.solo?.wins?.extra || 0) + (day.ad?.wins?.regular || 0) + (day.ad?.wins?.extra || 0),
      Losses: (day.solo?.loses?.regular || 0) + (day.solo?.loses?.extra || 0) + (day.ad?.loses?.regular || 0) + (day.ad?.loses?.extra || 0),
      'Wordly+ Purchases': day.wordlyPlusPurchases || 0,
    }));

    const soloData = [
      { name: "Regular Wins", value: aggregated.solo.wins.regular },
      { name: "Extra Wins", value: aggregated.solo.wins.extra },
      { name: "Regular Loss", value: aggregated.solo.loses.regular },
      { name: "Extra Loss", value: aggregated.solo.loses.extra },
    ].filter(d => d.value > 0);

    const adData = [
      { name: "Regular Wins", value: aggregated.ad.wins.regular },
      { name: "Extra Wins", value: aggregated.ad.wins.extra },
      { name: "Regular Loss", value: aggregated.ad.loses.regular },
      { name: "Extra Loss", value: aggregated.ad.loses.extra },
    ].filter(d => d.value > 0);

    const totalStats = {
      wins: aggregated.solo.wins.regular + aggregated.solo.wins.extra + aggregated.ad.wins.regular + aggregated.ad.wins.extra,
      losses: aggregated.solo.loses.regular + aggregated.solo.loses.extra + aggregated.ad.loses.regular + aggregated.ad.loses.extra,
    };

    return (
      <div className="stats-block">
        <div className="stats-header">
          <div>
            <h1>Performance Statistics</h1>
            <p style={{ color: 'var(--text-muted)' }}>Range: {props.statsRange.toUpperCase()} ending {props.selectedDate}</p>
          </div>
          <div className="controls-row">
            <div className="range-selector">
              {["1d", "7d", "30d", "1y"].map((range) => (
                <button
                  key={range}
                  className={`range-btn ${props.statsRange === range ? "active" : ""}`}
                  onClick={() => props.setStatsRange(range)}
                >
                  {range.toUpperCase()}
                </button>
              ))}
            </div>
            <div className="date-picker-container">
              <select
                className="year-dropdown"
                value={props.selectedDate.split("-")[2]}
                onChange={(e) => {
                  const parts = props.selectedDate.split("-");
                  props.setSelectedDate(`${parts[0]}-${parts[1]}-${e.target.value}`);
                }}
              >
                {[2024, 2025, 2026, 2027].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <button className="icon-btn" onClick={() => handleDateChange(1)}><FaArrowRight /></button>
              <input
                type="date"
                value={props.selectedDate.split("-").map(p => p.padStart(2, '0')).reverse().join("-")}
                onChange={(e) => {
                  let newFormat = e.target.value.split("-").reverse().join("-");
                  if (newFormat.startsWith("0") && newFormat[1] !== "-") newFormat = newFormat.slice(1);
                  props.setSelectedDate(newFormat.replace("-0", "-"));
                }}
              />
              <button className="icon-btn" onClick={() => handleDateChange(-1)}><FaArrowLeft /></button>
            </div>
          </div>
        </div>

        <div className="word-pool-stats-grid">
          <div className="stat-card" style={{ borderColor: 'var(--primary)' }}>
            <div className="label">Total Games</div>
            <div className="value">{totalStats.wins + totalStats.losses}</div>
          </div>
          <div className="stat-card" style={{ borderColor: 'var(--secondary)' }}>
            <div className="label">Total Wins</div>
            <div className="value">{totalStats.wins}</div>
          </div>
          <div className="stat-card" style={{ borderColor: 'var(--error)' }}>
            <div className="label">Total Losses</div>
            <div className="value">{totalStats.losses}</div>
          </div>
          <div className="stat-card" style={{ borderColor: 'var(--warning)' }}>
            <div className="label">Avg Win Rate</div>
            <div className="value">
              {totalStats.wins + totalStats.losses > 0
                ? `${Math.round((totalStats.wins / (totalStats.wins + totalStats.losses)) * 100)}%`
                : '0%'}
            </div>
          </div>
          <div className="stat-card" style={{ borderColor: '#a855f7' }}>
            <div className="label">💎 Wordly+ Purchases</div>
            <div className="value" style={{ color: '#a855f7' }}>{aggregated.wordlyPlusPurchases}</div>
          </div>
        </div>

        {props.statsRange !== "1d" && (
          <div className="chart-card trend-chart" style={{ marginBottom: '32px' }}>
            <h3>Engagement Trend ({props.statsRange.toUpperCase()})</h3>
            <div style={{ height: '300px', width: '100%', marginTop: '20px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    interval={props.statsRange === "1y" ? 30 : props.statsRange === "30d" ? 5 : 0}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <RechartsTooltip contentStyle={{ borderRadius: '12px', border: '2px solid var(--border)', boxShadow: 'var(--shadow)' }} />
                  <Legend />
                  <Line type="monotone" dataKey="Wins" stroke="var(--secondary)" strokeWidth={3} dot={props.statsRange === "1y" ? false : { r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="Losses" stroke="var(--error)" strokeWidth={3} dot={props.statsRange === "1y" ? false : { r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="Wordly+ Purchases" stroke="#a855f7" strokeWidth={2} strokeDasharray="4 2" dot={props.statsRange === "1y" ? false : { r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="chart-container">
          <div className="chart-card">
            <h3>Solo Game Performance</h3>
            <div style={{ height: '300px', width: '100%', marginTop: '20px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={soloData} innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value" minAngle={15}>
                    {soloData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <RechartsTooltip contentStyle={{
                    background: '#fff',
                    border: '2px solid var(--border)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="chart-card">
            <h3>Ad Game Performance</h3>
            <div style={{ height: '300px', width: '100%', marginTop: '20px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={adData} innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value" minAngle={15}>
                    {adData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <RechartsTooltip contentStyle={{
                    background: '#fff',
                    border: '2px solid var(--border)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderWordPoolBlock = () => {
    if (!data.words || !data.words.length) {
      return <div className="loading-overlay"><img src={loader} alt="Loading..." /></div>;
    }

    const unusedCount = data.words.filter((word) => word.status === "Unused").length;
    const usedCount = data.words.filter((word) => word.status === "Used").length;
    const duplicates = checkForDuplicates().length;
    const invalidCount = checkForInvalidWords().length;

    let displayedWords = [...data.words];

    if (wordSearch) {
      displayedWords = displayedWords.filter(word =>
        word.word.toLowerCase().includes(wordSearch.toLowerCase())
      );
    }

    displayedWords.sort((a, b) => {
      if (sortConfig.key === "word") {
        return sortConfig.direction === "asc"
          ? a.word.localeCompare(b.word)
          : b.word.localeCompare(a.word);
      } else if (sortConfig.key === "status") {
        const statusA = a.status === "Used" ? 1 : 0;
        const statusB = b.status === "Used" ? 1 : 0;
        return sortConfig.direction === "asc" ? statusB - statusA : statusA - statusB;
      }
      return 0;
    });

    return (
      <div className="word-pool-block">
        <h1>Word Pool Management</h1>

        <div className="word-pool-stats-grid">
          <div className="stat-card" style={{ borderRightColor: 'var(--primary)' }}>
            <div className="label">Unused Words</div>
            <div className="value">{unusedCount}</div>
          </div>
          <div className="stat-card" style={{ borderRightColor: 'var(--secondary)' }}>
            <div className="label">Used Words</div>
            <div className="value">{usedCount}</div>
          </div>
          <div className="stat-card" style={{ borderRightColor: 'var(--warning)' }}>
            <div className="label">Duplicates</div>
            <div className="value">{duplicates}</div>
          </div>
          <div className="stat-card" style={{ borderRightColor: 'var(--error)' }}>
            <div className="label">Invalid</div>
            <div className="value">{invalidCount}</div>
          </div>
        </div>

        <div className="word-actions-card">
          <h3>Bulk Actions</h3>
          <div className="word-actions-grid">
            <button className="danger-btn action-card" onClick={deleteDuplicates} disabled={duplicates === 0}>
              <span className="icon">🧹</span>
              <div className="text-content">
                <span className="btn-label">Delete Duplicates</span>
                <span className="btn-desc">{duplicates} found</span>
              </div>
            </button>
            <button className="danger-btn action-card" onClick={deleteInvalidWords} disabled={invalidCount === 0}>
              <span className="icon">⚠️</span>
              <div className="text-content">
                <span className="btn-label">Delete Invalid</span>
                <span className="btn-desc">{invalidCount} found</span>
              </div>
            </button>
            <button className="primary-btn action-card" onClick={() => downloadWords(data.words)}>
              <span className="icon">📥</span>
              <div className="text-content">
                <span className="btn-label">Download List</span>
                <span className="btn-desc">Save as .txt</span>
              </div>
            </button>
            <div className="file-upload-wrapper">
              <input type="file" id="file-upload" onChange={handleFileUpload} accept=".txt" hidden />
              <label htmlFor="file-upload" className="primary-btn action-card upload-label">
                <span className="icon">📤</span>
                <div className="text-content">
                  <span className="btn-label">Bulk Upload</span>
                  <span className="btn-desc">Select .txt</span>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="blockCard quick-add-card">
          <h3>Quick Add Word</h3>
          <div className="add-word-input-container">
            <input
              type="text"
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              placeholder="Enter Hebrew word..."
            />
            <button className="primary-btn" onClick={addWord}>
              <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>+</span> Add to Pool
            </button>
          </div>
        </div>

        <div className="search-box" style={{ margin: '20px 0', maxWidth: '400px' }}>
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search word pool..."
            value={wordSearch}
            onChange={(e) => setWordSearch(e.target.value)}
          />
        </div>

        <table className="blockTable">
          <thead>
            <tr>
              <th onClick={() => sortTable("word")} style={{ cursor: 'pointer' }}>
                Word {sortConfig.key === "word" && (sortConfig.direction === "asc" ? "↓" : "↑")}
              </th>
              <th onClick={() => sortTable("status")} style={{ cursor: 'pointer' }}>
                Status {sortConfig.key === "status" && (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr className="today-row" style={{ background: '#f0fff4' }}>
              <td style={{ fontWeight: 800, color: 'var(--secondary)' }}>{props.todaysWords?.solo || "..."}</td>
              <td><span className="status-pill unused">TODAY (1st)</span></td>
              <td style={{ textAlign: 'center' }}>
                <button className="icon-btn" onClick={() => changeWord("solo")} title="Change Word">
                  <IoIosRefresh size={22} style={{ color: 'var(--secondary)' }} />
                </button>
              </td>
            </tr>
            <tr className="today-row" style={{ background: '#fff9e6' }}>
              <td style={{ fontWeight: 800, color: 'var(--primary)' }}>{props.todaysWords?.ad || "..."}</td>
              <td><span className="status-pill unused" style={{ background: 'var(--primary)', color: 'white' }}>TODAY (2nd)</span></td>
              <td style={{ textAlign: 'center' }}>
                <button className="icon-btn" onClick={() => changeWord("ad")} title="Change Word">
                  <IoIosRefresh size={22} style={{ color: 'var(--primary)' }} />
                </button>
              </td>
            </tr>
            {displayedWords.slice(0, wordsPagination).map((word) => (
              <tr key={word.id}>
                <td style={{ fontWeight: 600, fontSize: '1.1rem' }}>{word.word}</td>
                <td>
                  <span className={`status-pill ${word.status === 'Used' ? 'used' : 'unused'}`}>
                    {word.status}
                  </span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button className="icon-btn" onClick={() => removeWord(word.id, word.status === "Used" ? "used_words" : "unused_words")}>
                    <IoMdClose size={20} style={{ color: 'var(--error)' }} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {displayedWords.length > wordsPagination && (
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button className="primary-btn" style={{ background: 'white', color: 'var(--primary)', border: '2px solid var(--primary)' }} onClick={() => loadMoreWords()}>Load More</button>
            <button className="primary-btn" style={{ background: 'white', color: 'var(--primary)', border: '2px solid var(--primary)' }} onClick={() => loadMoreWords(true)}>Load All</button>
          </div>
        )}
      </div>
    );
  };
  const checkForInvalidWords = () => {
    const invalidWords = (data.words || []).filter((wordObj) => {
      const charCount = {};
      for (const char of wordObj.word) {
        charCount[char] = (charCount[char] || 0) + 1;
        if (charCount[char] > 1) return true;
      }
      return false;
    });
    console.log("Invalid: " + invalidWords);

    return invalidWords;
  };

  const deleteInvalidWords = async () => {
    if (window.confirm("Are you sure you want to delete invalid words?")) {
      const invalidWords = checkForInvalidWords();

      const batch = writeBatch(firestore);

      invalidWords.forEach((wordObj) => {
        const collectionName =
          wordObj.status === "Used" ? "used_words" : "unused_words";
        batch.delete(doc(firestore, collectionName, wordObj.id));
      });

      await batch.commit();
    }
  };

  const changeWord = async (wordType) => {
    try {
      // Get the word from todays_words
      const docRef = doc(firestore, "todays_words", wordType);
      const docSnap = await getDoc(docRef);
      const word = docSnap.data().word;
      await addDoc(collection(firestore, "used_words"), { word: word });

      // If unused_words is empty, transfer all used_words into unused_words
      const unusedWordsSnapshot = await getDocs(
        collection(firestore, "unused_words")
      );
      if (unusedWordsSnapshot.empty) {
        const usedWordsSnapshot = await getDocs(
          collection(firestore, "used_words")
        );
        const usedWordsData = usedWordsSnapshot.docs.map((doc) => doc.data());
        const unusedWordsRef = collection(firestore, "unused_words");
        const batch = writeBatch(firestore);
        usedWordsData.forEach((word) =>
          batch.set(doc(unusedWordsRef), { word: word.word })
        );
        await batch.commit();
      }

      const unusedWords = unusedWordsSnapshot.docs.map((doc) => ({
        id: doc.id,
        word: doc.data().word,
      }));
      let randomIndex = Math.floor(Math.random() * unusedWords.length);
      let randomWord;
      do {
        randomWord = unusedWords[randomIndex];
        randomIndex = (randomIndex + 1) % unusedWords.length;
      } while (randomWord.word === word);

      // Update todays_words and delete the unused word
      await updateDoc(docRef, { word: randomWord.word });
      await deleteDoc(doc(firestore, "unused_words", randomWord.id));
    } catch (error) {
      console.error("Error changing word:", error);
    }
  };

  const checkForDuplicates = () => {
    const wordMap = new Map();

    data.words.forEach((word) => {
      if (!wordMap.has(word.word)) {
        wordMap.set(word.word, []);
      }
      wordMap.get(word.word).push(word);
    });

    const duplicates = [];
    wordMap.forEach((value) => {
      if (value.length > 1) {
        console.log(value);
        duplicates.push(value);
      }
    });
    console.log("Duplicates: " + duplicates);
    return duplicates;
  };

  const deleteDuplicates = async () => {
    if (window.confirm("Are you sure you want to delete duplicates?")) {
      const duplicates = checkForDuplicates();

      const batch = writeBatch(firestore);

      duplicates.forEach((duplicateGroup) => {
        let usedWord = duplicateGroup.find(
          (wordObj) => wordObj.status === "Used"
        );
        if (!usedWord) {
          usedWord = duplicateGroup[0];
        }

        duplicateGroup.forEach((wordObj, index) => {
          if (wordObj.id !== usedWord.id) {
            const collectionName =
              wordObj.status === "Used" ? "used_words" : "unused_words";
            batch.delete(doc(firestore, collectionName, wordObj.id));
          }
        });
      });

      await batch.commit();
    }
  };

  const downloadWords = () => {
    const text = data.words.map((word) => word.word).join("\n");
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    saveAs(blob, "words.txt");
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const words = text
        .split("\n")
        .map((word) => word.trim())
        .filter((word) => word);

      const batch = writeBatch(firestore);
      words.forEach((word) => {
        const wordRef = doc(collection(firestore, "unused_words"));
        batch.set(wordRef, { word });
      });

      await batch.commit();
    };
    reader.readAsText(file);
  };

  const renderUserDirectoryBlock = () => {
    if (!data || data.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
          <img src={loader} alt="Loading..." style={{ width: "40px", marginBottom: "16px" }} />
          <p>Fetching user library...</p>
        </div>
      );
    }

    const filteredUsers = data
      .filter((user) => {
        const matchesSearch = (user.username || "").toLowerCase().includes(userSearch.toLowerCase());
        const matchesFilter =
          userFilter === "all" ||
          (userFilter === "wordlyPlus" ? user.hasWordlyPlus : user.type === userFilter);
        return matchesSearch && matchesFilter;
      })
      .sort((a, b) => {
        let valA = a[userSort.key];
        let valB = b[userSort.key];

        if (userSort.key === "lastOnline") {
          valA = valA ? valA : 0;
          valB = valB ? valB : 0;
        }

        if (typeof valA === "string") {
          return userSort.direction === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }

        return userSort.direction === "asc" ? valA - valB : valB - valA;
      });

    const handleSort = (key) => {
      setUserSort((prev) => ({
        key,
        direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
      }));
    };

    return (
      <div className="directory-block">
        <div className="directory-header">
          <div className="controls-row">
            <div className="search-box">
              <FaUser className="search-icon" />
              <input
                type="text"
                placeholder="Search players..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>
            <div className="filter-box">
              <span>Account:</span>
              <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className="year-dropdown">
                <option value="all">All Types</option>
                <option value="google">Google</option>
                <option value="apple">Apple</option>
                <option value="anonymous">Anonymous</option>
                <option value="wordlyPlus">💎 Wordly+</option>
              </select>
            </div>
          </div>
          <div className="stats-mini">
            Showing <strong>{filteredUsers.length}</strong> players
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="empty-state" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)", background: "white", borderRadius: "16px", border: "1px solid var(--border)" }}>
            <ion-icon name="search-outline" style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.5 }}></ion-icon>
            <h2>No users found matching your criteria</h2>
            <p>Try adjusting your search or filters.</p>
          </div>
        ) : (
          <table className="blockTable">
            <thead>
              <tr>
                <th onClick={() => handleSort("username")} style={{ cursor: "pointer" }}>
                  Player {userSort.key === "username" && (userSort.direction === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("type")} style={{ cursor: "pointer" }}>
                  Login {userSort.key === "type" && (userSort.direction === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("wins")} style={{ cursor: "pointer" }}>
                  Wins {userSort.key === "wins" && (userSort.direction === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("combo")} style={{ cursor: "pointer" }}>
                  Combo {userSort.key === "combo" && (userSort.direction === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("lastOnline")} style={{ cursor: "pointer" }}>
                  Activity {userSort.key === "lastOnline" && (userSort.direction === "asc" ? "↑" : "↓")}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="user-info">
                      <span className="user-link">
                        {user.hasWordlyPlus && <span title="Wordly+" style={{ marginRight: '5px', fontSize: '0.85em' }}>💎</span>}
                        {user.username}
                      </span>
                      <span className="user-id-sub">{user.id}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-pill ${user.type}`}>
                      {user.type === "google" && <FaGoogle style={{ marginRight: '6px' }} />}
                      {user.type === "apple" && <FaApple style={{ marginRight: '6px' }} />}
                      {user.type}
                    </span>
                  </td>
                  <td style={{ fontWeight: 800, color: 'var(--secondary)' }}>{user.wins}</td>
                  <td style={{ fontWeight: 800, color: 'var(--primary)' }}>{user.combo}</td>
                  <td>
                    <div className="activity-cell">
                      <span className="date-main">{user.lastOnline ? new Date(user.lastOnline).toLocaleDateString() : "Never"}</span>
                      <span className="time-sub">
                        {user.lastOnline ? new Date(user.lastOnline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "---"}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  switch (type) {
    case "online":
      return (
        <div className="block">
          <h1>Online Players ({data.length})</h1>
          {renderOnlineBlock()}
        </div>
      );
    case "directory":
      return (
        <div className="block">
          {renderUserDirectoryBlock()}
        </div>
      );
    case "stats":
      return (
        <div className="block">
          {renderStatsBlock()}
        </div>
      );

    case "words":
      return (
        <div className="block">
          <h1>Today's Secret Words</h1>
          {renderTodaysWordsBlock()}
        </div>
      );
    case "wordPool":
      return (
        <div className="block">
          {renderWordPoolBlock()}
        </div>
      );
    default:
      return <div />;
  }
};

export default Block;
